import Autocomplete, {
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import React, { useState, useMemo } from 'react';
import {
  InputText,
  InputType,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteChangeReason,
  Box,
  ListItem,
  styled,
  SxProps,
  TextField,
} from '@mui/material';
import {
  GroupType,
  OrganisationResponse,
  useGetGroup,
  type PagedOrganisationResponse,
} from 'api';
import { useGetPaginatedOrganisations } from 'api/organisations/useGetPaginatedOrganisations';
import { INSIGHTS_ORGANISATION_ID } from 'variables/constants';
import { FullScreenError } from 'components/FullScreen';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import { PaginationContext } from 'components/VirtualisedAutocomplete/autoComplete.helper';
import PaginatedListBoxComponent from 'components/VirtualisedAutocomplete/PaginatedListBoxComponent';
import { GroupText } from './GroupText';

const ErrorMessage = styled('p')(({ theme }) => ({
  ...theme.typography.caption,
  color: `${theme.palette.error.main}`,
  margin: '3px 14px 0 14px',
}));

const STANDARD = 'standard';
const DROPDOWN = 'dropdown';
const MULTI_SELECT = 'multi-select';
type Variant = typeof STANDARD | typeof DROPDOWN | typeof MULTI_SELECT;

type OrganisationAutocompleteProps = {
  onSelect: (value: OrganisationResponse) => void;
  className?: string;
  disableClearable?: boolean;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: boolean;
  currentOrg?: OrganisationResponse;
  excludedOrganisationIds?: string[];
  disabled?: boolean;
  labelKey?: string;
  inputSx?: SxProps;
  groupId?: string;
  dataTestId?: string;
  variant?: Variant;
  onlySpecialHandling?: boolean;
};

const StyledInput = styled(InputText)(({ theme }) => ({
  '& > div': {
    backgroundColor: theme.palette.background.paper,
    height: '3rem',
    width: '100%',
  },
}));

function preventFormSubmission(event: React.KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'Enter') event.preventDefault();
}

const SELECT_OPTION: AutocompleteChangeReason = 'selectOption';

const OrganisationAutocomplete = ({
  onSelect,
  disableClearable = false,
  required,
  error = false,
  className,
  currentOrg,
  excludedOrganisationIds,
  helperText,
  disabled,
  inputSx,
  labelKey = 'ui.event-management.organisations.select-organisation',
  groupId,
  dataTestId,
  variant = STANDARD,
  onlySpecialHandling = false,
}: OrganisationAutocompleteProps) => {
  const { t } = useTranslation();

  const clearOnSelect = variant === MULTI_SELECT;

  const [selectedOrg, setSelectedOrg] = useState<OrganisationResponse | null>(
    currentOrg || null,
  );
  const [inputValue, setInputValue] = useState<string>(currentOrg?.name || '');
  const debouncedInputValue = useDebounceValue(inputValue);

  const { data: group, status: groupStatus } = useGetGroup(groupId || '', {
    enabled: !!groupId,
  });

  const {
    status,
    isFetching: paginatedLoading,
    data: { items: paginatedOrganisations = [] } = {},
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = useGetPaginatedOrganisations({
    searchTerm: debouncedInputValue,
    enabled: !groupId,
  });

  const organisations: PagedOrganisationResponse[] = group
    ? group.organisations
    : paginatedOrganisations;

  const isLoading = groupId ? groupStatus !== 'success' : paginatedLoading;
  const isErrored = status === 'error' || groupStatus === 'error';

  const selectedItem = selectedOrg || currentOrg || null;

  const sortedAndFilteredOrganisations = useMemo(() => {
    const insightsOrg = organisations.find(
      (org) => org.id === INSIGHTS_ORGANISATION_ID,
    );
    const nonInsightsOrgs = organisations.filter(
      (org) => org.id !== INSIGHTS_ORGANISATION_ID,
    );

    /**
     * If the selected item is not in the search results, we add it back to the list.
     *
     * This is so that MUI Autocomplete does not show the console error:
     * > MUI: The value provided to Autocomplete is invalid. None of the options match with ...
     */
    const selectedItemMissing =
      selectedItem && !organisations.some((org) => org.id === selectedItem.id);
    const otherOrgs: PagedOrganisationResponse[] = selectedItemMissing
      ? [selectedItem, ...nonInsightsOrgs]
      : nonInsightsOrgs;
    /**
     * The selected item cannot be excluded from the list of organisations,
     * even if it is in the excludedOrganisationIds list.
     * Otherwise MUI Autocomplete will show the a console error: See above.
     */
    const nonSelectedExcludedOrgIds = excludedOrganisationIds?.filter(
      (id) => id !== selectedItem?.id,
    );

    const sortedOrgs = insightsOrg
      ? [insightsOrg, ...otherOrgs]
      : organisations;
    const hasExclusions =
      !nonSelectedExcludedOrgIds || nonSelectedExcludedOrgIds.length <= 0;

    return sortedOrgs.filter(
      ({ id, visibility }) =>
        (hasExclusions || !nonSelectedExcludedOrgIds.includes(id)) &&
        (!onlySpecialHandling || visibility === 'PRIVATE'),
    );
  }, [
    excludedOrganisationIds,
    onlySpecialHandling,
    organisations,
    selectedItem,
  ]);

  /**
   * We only need to control inputValue when using the design library InputText; not when using MUI TextField.
   * See: https://mui.com/material-ui/react-autocomplete/#controlled-states
   */
  const isControllingInputValueState = variant !== DROPDOWN;

  const inputProps = useMemo(
    () =>
      isControllingInputValueState
        ? {
            inputValue,
            onInputChange: (e: any) => {
              const value = e?.target?.value || '';
              setInputValue(value);
            },
            renderInput: (params: AutocompleteRenderInputParams) => {
              const { size, id, ...rest } = params;
              const { ref } = rest.slotProps.input;
              const { children, ...props } = rest.slotProps.htmlInput;
              return (
                <Box ref={ref}>
                  <StyledInput
                    data-testid={`${dataTestId}-input`}
                    {...props}
                    id={id}
                    type={InputType.TEXT}
                    label={t(labelKey)}
                    placeholder={t(
                      'ui.event-management.organisations.select-organisation-placeholder',
                    )}
                    required={required}
                    disabled={disabled}
                    error={error}
                    onKeyDown={preventFormSubmission}
                    sx={inputSx}
                  />
                </Box>
              );
            },
          }
        : {
            onInputChange: (e: any) => {
              const value = e?.target?.value || '';
              setInputValue(value);
            },
            renderInput: (params: AutocompleteRenderInputParams) => {
              return (
                <TextField
                  data-testid={`${dataTestId}-input`}
                  {...params}
                  label={t(labelKey)}
                  placeholder={t(
                    'ui.event-management.organisations.select-organisation-placeholder',
                  )}
                  required={required}
                  disabled={disabled}
                  error={error}
                  onKeyDown={preventFormSubmission}
                  variant="outlined"
                />
              );
            },
          },
    [
      inputValue,
      labelKey,
      isControllingInputValueState,
      dataTestId,
      disabled,
      error,
      inputSx,
      required,
      t,
    ],
  );

  if (isErrored) {
    return (
      <FullScreenError
        message={t('ui.event-management.organisations.error-loading')}
      />
    );
  }

  const typeTranslations: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.organisations.type.customer'),
    PARTNER: t('ui.event-management.organisations.type.partner'),
  };

  return (
    <>
      <PaginationContext.Provider
        value={
          groupId
            ? { hasMore: false, isFetching: false, loadMore: () => {} }
            : {
                hasMore,
                isFetching: paginatedLoading,
                loadMore,
              }
        }
      >
        <Autocomplete
          className={className}
          id="organisation-search"
          data-testid={dataTestId || 'organisation-autocomplete'}
          disableClearable={disableClearable}
          autoSelect={disableClearable}
          options={sortedAndFilteredOrganisations}
          fullWidth
          loadingText={t('ui.event-management.organisations.loading-text')}
          noOptionsText={t('ui.event-management.organisations.no-options-text')}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          {...inputProps}
          value={selectedItem}
          onChange={(_, value, reason) => {
            if (reason === SELECT_OPTION && value !== null) {
              setSelectedOrg(value);
              onSelect(value);

              if (clearOnSelect) {
                setInputValue('');
                onSelect({
                  id: '',
                  name: '',
                  dataRetentionPolicy: { maximumInactivityPeriod: 'P5Y3M2D' },
                });
                setSelectedOrg(null);
              } else {
                setInputValue(value.name);
              }
            }
          }}
          filterOptions={(listOptions) => listOptions}
          getOptionLabel={(option) => option.name || ''}
          renderOption={(fullProps, val) => {
            const { color, ...rest } = fullProps;
            return (
              <ListItem
                {...rest}
                key={val.id}
                data-testid={`organisation-option-${val.id}`}
                sx={{
                  alignItems: 'flex-start !important',
                }}
              >
                <TextV2
                  component="p"
                  sx={{
                    textAlign: 'left',
                    width: '50%',
                    wordWrap: 'break-word',
                  }}
                  variant="bodyBold"
                  color="primary"
                >
                  {val.name}
                </TextV2>

                {val.organisationGroup ? (
                  <TextV2
                    component="p"
                    sx={{
                      textAlign: 'right',
                      width: '50%',
                      wordWrap: 'break-word',
                    }}
                    color="textSecondary"
                  >
                    <GroupText
                      groupName={val.organisationGroup.name}
                      groupType={typeTranslations[val.organisationGroup.type]}
                    />
                  </TextV2>
                ) : null}
              </ListItem>
            );
          }}
          slots={{ listbox: PaginatedListBoxComponent }}
          loading={isLoading}
        />
      </PaginationContext.Provider>
      {helperText ? <ErrorMessage>{helperText}</ErrorMessage> : null}
    </>
  );
};

export default OrganisationAutocomplete;
